import React, { useState, useEffect, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import '@/App.css';
import { AuthProvider, useAuth, API, BACKEND_URL, apiClient } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import ErrorBoundary from './components/ErrorBoundary';
import logger from './lib/logger';
import { getErrorMessage } from './lib/errorHandler';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import Mantenimiento from './pages/Mantenimiento';
import { Toaster } from './components/ui/sonner';
import ConfirmDialog from './components/ConfirmDialog';
import HabeasDataModal from './components/HabeasDataModal';
import PagoVencidoScreen from './components/PagoVencidoScreen';
import MaintenanceCountdownModal from './components/MaintenanceCountdownModal';
import NovedadesVersionModal from './components/NovedadesVersionModal';
import AvisoPruebaModal from './components/AvisoPruebaModal';
import { APP_VERSION } from './version';

// Lazy loading para páginas menos frecuentes (reducir bundle inicial)
const NuevaCuentaCobro = React.lazy(() => import('./pages/NuevaCuentaCobro'));
const EditarCuentaCobro = React.lazy(() => import('./pages/EditarCuentaCobro'));
const NuevaPostCuenta = React.lazy(() => import('./pages/NuevaPostCuenta'));
const EditarPostCuenta = React.lazy(() => import('./pages/EditarPostCuenta'));
const Ajustes = React.lazy(() => import('./pages/Ajustes'));
const Perfil = React.lazy(() => import('./pages/Perfil'));
const Bitacora = React.lazy(() => import('./pages/Bitacora'));

// Componente interno que usa el AuthContext
const AppContent = () => {
  const { user, logout, isAdmin, loading, supportUser, stopSupport, isSupportMode, confirmConfig, maintenanceMode, loginMaintenance, loadUser, showNotification } = useAuth();
  const [configuracion, setConfiguracion] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showNovedades, setShowNovedades] = useState(false);
  // Estado de pago del usuario. Se carga al loguearse; si bloqueado===true, se
  // muestra PagoVencidoScreen y se bloquea el acceso al resto de la app.
  const [pagoEstado, setPagoEstado] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  // Cargar estado de pago cuando el usuario se loguea (o cambia)
  useEffect(() => {
    let cancelled = false;
    const cargarEstadoPago = async () => {
      if (!user) {
        setPagoEstado(null);
        return;
      }
      // En modo soporte no aplica bloqueo (el admin no debe ser bloqueado por
      // el pago del usuario soportado).
      if (isSupportMode) {
        setPagoEstado(null);
        return;
      }
      // Admin nunca se bloquea; igual cargamos por si la UI lo necesita
      try {
        const resp = await apiClient.get('/auth/me-pago');
        if (!cancelled) setPagoEstado(resp.data);
      } catch (e) {
        // Si falla, no bloqueamos (no queremos cortar acceso por error de red)
        if (!cancelled) setPagoEstado(null);
      }
    };
    cargarEstadoPago();
    // Re-chequear cada 5 min por si el admin registra un pago mientras el
    // usuario tiene la app abierta
    const interval = setInterval(cargarEstadoPago, 5 * 60 * 1000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [user, isSupportMode]);

  // Aviso de fin de periodo de prueba: usuarios EN prueba con <=5 días restantes.
  const [avisoPruebaVisto, setAvisoPruebaVisto] = useState(false);       // modal: 1 vez por sesión
  const [bannerPruebaCerrado, setBannerPruebaCerrado] = useState(false); // X del banner
  useEffect(() => {
    setAvisoPruebaVisto(false);
    setBannerPruebaCerrado(false);
  }, [user?.id]);

  const enAvisoPrueba = !!pagoEstado
    && pagoEstado.estado === 'en_prueba'
    && typeof pagoEstado.dias_restantes === 'number'
    && pagoEstado.dias_restantes <= 5
    && !isSupportMode
    && user?.rol !== 'admin';

  // Header colapsable: en páginas de edición de evidencias (mucho contenido)
  // el header se autocomprime para dar más espacio vertical. Se expande con
  // hover, o con el botón de pin que lo deja fijo.
  const rutasEdicion = ['/nueva', '/editar/', '/post-cuentas/nueva', '/post-cuentas/editar/'];
  const esRutaEdicion = rutasEdicion.some(r => location.pathname.startsWith(r));
  const [headerPinned, setHeaderPinned] = useState(!esRutaEdicion);
  const [headerHovered, setHeaderHovered] = useState(false);
  const headerExpanded = headerPinned || headerHovered;

  // Cuando cambia la ruta, ajustar el estado pinned acorde
  useEffect(() => {
    setHeaderPinned(!esRutaEdicion);
  }, [esRutaEdicion]);


  useEffect(() => {
    if (user) {
      cargarConfiguracion();
    }
  }, [user, supportUser]);

  const cargarConfiguracion = async () => {
    try {
      const response = await apiClient.get('/configuracion');
      setConfiguracion(response.data || {});
    } catch (error) {
      logger.error('Error cargando configuración:', error);
      setConfiguracion({});
    }
  };

  const isConfigIncomplete = user && configuracion !== null && (
    !configuracion.cedula || 
    configuracion.cedula.trim() === '' || 
    !configuracion.obligaciones || 
    configuracion.obligaciones.length === 0 ||
    configuracion.obligaciones.some(obl => !obl.titulo || obl.titulo.trim() === '')
  );

  useEffect(() => {
    // Redirigir si la configuración está incompleta, no estamos en la página de config,
    // no estamos cargando, y NO estamos en modo soporte (el admin no debe ser bloqueado por la config del usuario que soporta)
    if (user && !loading && isConfigIncomplete && location.pathname !== '/configuracion' && !isSupportMode) {
      navigate('/configuracion');
    }
  }, [user, loading, isConfigIncomplete, location.pathname, isSupportMode, navigate]);

  // Habeas Data: mostrar modal la primera vez que el usuario entra (si no ha aceptado el tratamiento de datos)
  // No aplica en modo soporte (el admin no debe ser bloqueado por la decisión pendiente del usuario soportado)
  const debeMostrarHabeasData = !!user && user.acepto_tratamiento_datos === false && !isSupportMode;

  const handleAceptarHabeasData = async () => {
    try {
      await apiClient.post('/auth/aceptar-tratamiento-datos');
      await loadUser();
      showNotification('Gracias. Bienvenido a IMEC.', 'success');
    } catch (e) {
      logger.error('Error aceptando tratamiento de datos:', e);
      showNotification(getErrorMessage(e, 'Error al registrar la aceptación. Intenta nuevamente.'), 'error');
      throw e;
    }
  };

  const handleRechazarHabeasData = async () => {
    showNotification('Debes aceptar el tratamiento de datos para usar la aplicación.', 'warning');
    await logout();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando...</p>
        </div>
      </div>
    );
  }

  // 🛠️ Lógica de Mantenimiento Refinada con Grace Period:
  // 1. Si NO hay usuario y vinimos de un error 503 → mostrar aviso.
  // 2. Si usuario no-admin y mantenimiento YA vigente (seconds_remaining<=0) → bloqueo total.
  // 3. Si usuario no-admin y hay grace period activo (seconds_remaining>0) →
  //    permitir continuar trabajando, mostrar modal countdown que forzará logout.
  const graceRemaining = maintenanceMode?.seconds_remaining;
  const enGracePeriod = !!user && !isAdmin && maintenanceMode?.active && typeof graceRemaining === 'number' && graceRemaining > 0;
  const mantenimientoVigente = maintenanceMode?.active && (
    (!user && loginMaintenance) ||
    (user && !isAdmin && (typeof graceRemaining !== 'number' || graceRemaining <= 0))
  );

  // Si el usuario no-admin tiene pago vencido, bloquear el acceso a la app.
  // Admin nunca se bloquea. En modo soporte tampoco aplica.
  if (user && !isAdmin && !isSupportMode && pagoEstado?.bloqueado) {
    return <PagoVencidoScreen estado={pagoEstado} />;
  }

  if (mantenimientoVigente) {
    return <Mantenimiento />;
  }

  const { notification } = useAuth();

  const SupportBanner = () => {
    if (!isSupportMode) return null;

    return (
      <div className="bg-orange-600 text-white py-3 px-4 flex justify-between items-center shadow-lg sticky top-0 z-[60] border-b border-orange-700">
        <div className="max-w-7xl mx-auto w-full flex justify-between items-center">
          <div className="flex items-center gap-4">
            <span className="animate-pulse text-2xl">⚠️</span>
            <div className="text-left">
              <p className="font-bold uppercase tracking-tight text-xs sm:text-sm text-orange-100 leading-none mb-1">
                Modo Soporte Técnico Activo
              </p>
              <p className="font-black text-sm sm:text-lg">
                Gestionando a: <span className="bg-white text-orange-700 px-2 py-0.5 rounded ml-1">{supportUser.nombre_completo}</span>
              </p>
            </div>
          </div>
          <button
            onClick={() => {
              stopSupport();
              window.location.href = '/';
            }}
            className="bg-white text-orange-700 hover:bg-orange-50 px-6 py-2 rounded-lg font-bold text-sm transition shadow-md hover:scale-105 active:scale-95"
          >
            Finalizar Soporte
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-blue-50">
      <SupportBanner />
      {/* Banner de aviso de fin de prueba (<=5 días). Se cierra con ✕ pero
          reaparece al recargar; desaparece solo al pagar o ampliar la prueba. */}
      {user && enAvisoPrueba && !bannerPruebaCerrado && (
        <div className="bg-amber-500 text-white py-2.5 px-4 sticky top-0 z-[55] shadow-md">
          <div className="max-w-7xl mx-auto w-full flex items-center justify-between gap-3">
            <p className="text-sm font-medium flex items-center gap-2">
              <span className="text-lg">⏳</span>
              Tu periodo de prueba vence en {pagoEstado.dias_restantes} día{pagoEstado.dias_restantes === 1 ? '' : 's'}.
              Comunícate con el administrador para ampliar tu licencia.
            </p>
            <button
              onClick={() => setBannerPruebaCerrado(true)}
              className="shrink-0 text-white/90 hover:text-white hover:bg-white/20 rounded-lg w-7 h-7 flex items-center justify-center font-bold transition"
              title="Cerrar"
            >
              ✕
            </button>
          </div>
        </div>
      )}
      {/* Header - Solo si está autenticado */}
      {user && (
        <header
          className="bg-white shadow-md sticky top-0 z-40 transition-all duration-300"
          onMouseEnter={() => !headerPinned && setHeaderHovered(true)}
          onMouseLeave={() => setHeaderHovered(false)}
        >
          <div className="max-w-7xl mx-auto">
            <div className={`flex items-center justify-between px-4 sm:px-6 transition-all duration-300 ${headerExpanded ? 'py-4' : 'py-1.5'}`}>
              <Link to="/" className="flex items-center space-x-3 sm:space-x-4 group">
                <div className="flex items-center gap-4 sm:gap-4">
                  <div className={`bg-blue-600 rounded-2xl shadow-lg transform transition-all duration-300 group-hover:scale-105 ${headerExpanded ? 'p-2 sm:p-3' : 'p-1.5'}`}>
                    <svg className={`text-white transition-all duration-300 ${headerExpanded ? 'w-8 h-8 sm:w-12 sm:h-12' : 'w-5 h-5'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <div>
                    <div className={`font-black text-blue-900 tracking-tight leading-none transition-all duration-300 ${headerExpanded ? 'text-3xl sm:text-5xl mb-1' : 'text-xl'}`}>
                      IMEC
                      {isAdmin ? (
                        <button
                          type="button"
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowNovedades(true); }}
                          className={`ml-2 font-semibold text-blue-500 hover:text-blue-700 hover:underline tracking-normal cursor-pointer transition-colors ${headerExpanded ? 'text-sm' : 'text-[10px]'}`}
                          title={`Ver novedades de la versión ${APP_VERSION}`}
                        >
                          v{APP_VERSION}
                        </button>
                      ) : (
                        <span
                          className={`ml-2 font-semibold text-blue-500 tracking-normal ${headerExpanded ? 'text-sm' : 'text-[10px]'}`}
                          title={`Versión ${APP_VERSION}`}
                        >
                          v{APP_VERSION}
                        </span>
                      )}
                    </div>
                    {headerExpanded && (
                      <div className="hidden sm:block text-[11px] font-bold text-blue-600 uppercase tracking-widest leading-none">
                        Informes Mensuales de Ejecución Contractual
                      </div>
                    )}
                  </div>
                </div>
              </Link>

              {/* Menú Desktop - Botones de Acción (oculto cuando header colapsado en rutas de edición) */}
              {headerExpanded && (
              <div className={`hidden lg:flex items-center bg-slate-100/50 p-1 rounded-xl border border-slate-200 ${isConfigIncomplete ? 'opacity-50 pointer-events-none' : ''}`}>
                <Link
                  to="/"
                  className={`h-9 px-4 rounded-lg flex items-center gap-2 text-sm font-bold transition-all whitespace-nowrap active:scale-95 ${location.pathname === '/' && !location.search.includes('tab=GF')
                    ? 'bg-white shadow-md text-blue-700'
                    : 'text-slate-600 hover:bg-white/60 hover:text-slate-900'
                    }`}
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                  </svg>
                  Dashboard
                </Link>
                <div className="w-px h-4 bg-slate-300 mx-1"></div>
                <Link
                  to="/nueva"
                  className={`h-9 px-4 rounded-lg flex items-center gap-2 text-sm font-bold transition-all whitespace-nowrap active:scale-95 ${location.pathname === '/nueva'
                    ? 'bg-blue-600 text-white shadow-lg'
                    : 'text-blue-600 hover:bg-blue-50 shadow-sm hover:shadow-md'
                    }`}
                >
                  <span className="text-lg">🧩</span> Nuevo GC
                </Link>
                <Link
                  to="/?tab=GF"
                  className={`h-9 px-4 rounded-lg flex items-center gap-2 text-sm font-bold transition-all whitespace-nowrap active:scale-95 ${location.search.includes('tab=GF')
                    ? 'bg-indigo-600 text-white shadow-lg'
                    : 'text-indigo-600 hover:bg-indigo-50 shadow-sm hover:shadow-md'
                    }`}
                >
                  <span className="text-lg">🧩</span> Nuevo GF
                </Link>
                <Link
                  to="/post-cuentas/nueva"
                  className={`h-9 px-4 rounded-lg flex items-center gap-2 text-sm font-bold transition-all whitespace-nowrap active:scale-95 ${location.pathname === '/post-cuentas/nueva'
                    ? 'bg-purple-600 text-white shadow-lg'
                    : 'text-purple-600 hover:bg-purple-50 shadow-sm hover:shadow-md'
                    }`}
                >
                  <span className="text-lg">📋</span> Nueva PC
                </Link>
              </div>
              )}

              {/* Botón pin/colapsar header — solo en rutas de edición */}
              {esRutaEdicion && (
                <button
                  onClick={() => setHeaderPinned(!headerPinned)}
                  className={`hidden lg:flex items-center justify-center p-2 rounded-lg transition-all ${
                    headerPinned
                      ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                      : 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                  }`}
                  title={headerPinned ? 'Fijar header: SÍ (clic para autocolapsar)' : 'Header autocolapsable (clic para fijar)'}
                >
                  {headerPinned ? (
                    // Icono pin lleno (fijo)
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9 4v8.586l-2.293-2.293a1 1 0 00-1.414 1.414l4 4a1 1 0 001.414 0l4-4a1 1 0 00-1.414-1.414L11 12.586V4a1 1 0 10-2 0z" />
                    </svg>
                  ) : (
                    // Icono chevron up (colapsable)
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                    </svg>
                  )}
                </button>
              )}

              {/* Menú Desktop - Utilidades */}
              <div className="hidden lg:flex items-center space-x-1">
                {headerExpanded && isAdmin && (
                <Link
                  to="/bitacora"
                  className="p-2 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                  title="Bitácora de la aplicación (solo admin)"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                </Link>
                )}
                {headerExpanded && (
                <Link
                  to="/configuracion"
                  className="p-2 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                  title="Ajustes y Administración"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </Link>
                )}

                {/* Nombre del usuario conectado — versión compacta cuando colapsado */}
                {headerExpanded ? (
                  <div className="flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-lg">
                    <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold">
                      {user?.nombre_completo?.charAt(0).toUpperCase()}
                    </div>
                    <div className="text-left">
                      <div className="text-sm font-semibold text-gray-900">{user?.nombre_completo}</div>
                      <div className="text-xs text-gray-500">{isAdmin ? 'Administrador' : 'Usuario'}</div>
                    </div>
                  </div>
                ) : (
                  <div
                    className="w-7 h-7 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-xs"
                    title={user?.nombre_completo}
                  >
                    {user?.nombre_completo?.charAt(0).toUpperCase()}
                  </div>
                )}

                <Link
                  to="/perfil"
                  className="px-4 py-2 text-blue-700 hover:text-blue-900 font-medium transition"
                  title="Mi Perfil"
                >
                  👤
                </Link>
                <button
                  onClick={logout}
                  className={`bg-red-50 text-red-600 hover:bg-red-600 hover:text-white rounded-xl font-bold transition-all flex items-center gap-2 active:scale-95 ${
                    headerExpanded ? 'h-10 px-4' : 'h-8 px-2'
                  }`}
                  title="Salir"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  {headerExpanded && 'Salir'}
                </button>
              </div>

              {/* Menú Móvil - Botón Hamburguesa */}
              <button
                onClick={() => setMenuOpen(!menuOpen)}
                className="lg:hidden p-2 text-gray-600 hover:text-gray-900"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {menuOpen ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  )}
                </svg>
              </button>
            </div>

            {/* Menú Móvil Desplegable */}
            {menuOpen && (
              <div className="lg:hidden border-t border-gray-200 bg-white">
                <div className="px-4 py-2 space-y-2">
                  {/* Usuario conectado - Móvil */}
                  <div className="flex items-center gap-3 px-4 py-3 bg-gray-100 rounded-lg mb-2">
                    <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                      {user?.nombre_completo?.charAt(0).toUpperCase()}
                    </div>
                    <div className="text-left">
                      <div className="text-sm font-semibold text-gray-900">{user?.nombre_completo}</div>
                      <div className="text-xs text-gray-500">{isAdmin ? 'Administrador' : 'Usuario'}</div>
                    </div>
                  </div>

                  {!isConfigIncomplete ? (
                    <>
                      <Link
                        to="/"
                        onClick={() => setMenuOpen(false)}
                        className="flex items-center gap-3 px-4 py-3 bg-slate-100 text-slate-700 rounded-xl font-bold transition-all active:scale-95 shadow-sm"
                      >
                        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                        </svg>
                        Dashboard
                      </Link>
                      <Link
                        to="/nueva"
                        onClick={() => setMenuOpen(false)}
                        className="flex items-center gap-3 px-4 py-3 bg-blue-600 text-white rounded-xl font-bold transition-all active:scale-95 shadow-sm"
                      >
                        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Nueva Cuenta
                      </Link>
                      <Link
                        to="/post-cuentas/nueva"
                        onClick={() => setMenuOpen(false)}
                        className="flex items-center gap-3 px-4 py-3 bg-purple-600 text-white rounded-xl font-bold transition-all active:scale-95 shadow-sm"
                      >
                        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Nueva Post Cuenta
                      </Link>
                    </>
                  ) : (
                    <div className="p-4 bg-orange-50 border border-orange-200 rounded-xl text-orange-800 text-sm font-medium">
                      ⚠️ Debe completar su configuración antes de usar el sistema.
                    </div>
                  )}

                  <Link
                    to="/configuracion"
                    onClick={() => setMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 bg-blue-50 text-blue-700 rounded-xl font-bold transition-all active:scale-95 shadow-sm"
                  >
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    </svg>
                    Ajustes y Admin
                  </Link>
                  <Link
                    to="/perfil"
                    onClick={() => setMenuOpen(false)}
                    className="block px-4 py-2 text-blue-700 hover:bg-blue-50 rounded"
                  >
                    👤 Perfil
                  </Link>
                  {isAdmin && (
                    <Link
                      to="/bitacora"
                      onClick={() => setMenuOpen(false)}
                      className="block px-4 py-2 text-blue-700 hover:bg-blue-50 rounded"
                    >
                      📖 Bitácora
                    </Link>
                  )}
                  <button
                    onClick={() => {
                      logout();
                      setMenuOpen(false);
                    }}
                    className="block w-full text-left px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                  >
                    Salir
                  </button>
                </div>
              </div>
            )}
          </div>
        </header>
      )}

      {/* Main Content */}
      <main className="max-w-7xl mx-auto py-4 sm:py-8">
        <Routes>
          {/* Rutas Públicas */}
          <Route path="/login" element={user ? <Navigate to="/" /> : <Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />

          {/* Rutas Protegidas */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/configuracion"
            element={
              <ProtectedRoute>
                <Ajustes />
              </ProtectedRoute>
            }
          />
          <Route
            path="/nueva"
            element={
              <ProtectedRoute>
                <NuevaCuentaCobro configuracion={configuracion} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/editar/:id"
            element={
              <ProtectedRoute>
                <EditarCuentaCobro configuracion={configuracion} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/post-cuentas/nueva"
            element={
              <ProtectedRoute>
                <NuevaPostCuenta configuracion={configuracion} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/post-cuentas/editar/:id"
            element={
              <ProtectedRoute>
                <EditarPostCuenta configuracion={configuracion} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/perfil"
            element={
              <ProtectedRoute>
                <Perfil />
              </ProtectedRoute>
            }
          />
          <Route
            path="/bitacora"
            element={
              <ProtectedRoute requireAdmin>
                <Bitacora />
              </ProtectedRoute>
            }
          />

          {/* Redirección por defecto */}
          <Route path="*" element={<Navigate to={user ? "/" : "/login"} />} />
        </Routes>
        <Toaster richColors closeButton position="bottom-right" expand={true} />
        {confirmConfig && <ConfirmDialog isOpen={!!confirmConfig} {...confirmConfig} />}
        {debeMostrarHabeasData && (
          <HabeasDataModal
            isOpen={true}
            onAccept={handleAceptarHabeasData}
            onReject={handleRechazarHabeasData}
            userName={user?.nombre_completo}
          />
        )}
        {/* Modal countdown cuando admin activa mantenimiento con grace period */}
        {enGracePeriod && (
          <MaintenanceCountdownModal
            isOpen={true}
            initialSeconds={graceRemaining}
            onLogout={async () => {
              try { await logout(); } catch (e) { /* ignore */ }
              navigate('/login');
            }}
          />
        )}
        {/* Modal de novedades de la versión actual (clic en v1.5.0 del header) */}
        <NovedadesVersionModal
          isOpen={showNovedades}
          onClose={() => setShowNovedades(false)}
        />
        {/* Aviso de fin de prueba: modal una vez por sesión cuando quedan <=5 días */}
        {enAvisoPrueba && !avisoPruebaVisto && (
          <AvisoPruebaModal
            dias={pagoEstado.dias_restantes}
            pruebaHasta={pagoEstado.prueba_hasta}
            onClose={() => setAvisoPruebaVisto(true)}
          />
        )}
      </main>
    </div>
  );
};

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="text-gray-500">Cargando...</div></div>}>
            <AppContent />
          </Suspense>
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;